import { useState, useCallback, useRef } from 'react';

export interface SwipeState {
  x: number;
  y: number;
  rotation: number;
  opacity: number;
  direction: 'left' | 'right' | 'none';
  isDragging: boolean;
  isAnimating: boolean;
}

export interface SwipeHandlers {
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchMove: (e: React.TouchEvent) => void;
  onTouchEnd: () => void;
  onMouseDown: (e: React.MouseEvent) => void;
  onMouseMove: (e: React.MouseEvent) => void;
  onMouseUp: () => void;
  onMouseLeave: () => void;
}

export interface UseSwipeOptions {
  threshold?: number; // minimum swipe distance to trigger action
  velocityThreshold?: number; // velocity threshold for quick swipes
  rotationMultiplier?: number; // how much to rotate based on drag
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeStart?: () => void;
  onSwipeEnd?: () => void;
  disabled?: boolean;
}

const DEFAULT_OPTIONS: UseSwipeOptions = {
  threshold: 80, // Reduced for quicker swipes
  velocityThreshold: 0.4, // More sensitive to quick gestures
  rotationMultiplier: 0.08, // Subtler rotation for polish
};

export function useSwipe(options: UseSwipeOptions = {}): [SwipeState, SwipeHandlers, () => void] {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  
  const [state, setState] = useState<SwipeState>({
    x: 0,
    y: 0,
    rotation: 0,
    opacity: 1,
    direction: 'none',
    isDragging: false,
    isAnimating: false,
  });

  const startPos = useRef({ x: 0, y: 0 });
  const startTime = useRef(0);
  const lastPos = useRef({ x: 0, y: 0 });

  const reset = useCallback(() => {
    setState({
      x: 0,
      y: 0,
      rotation: 0,
      opacity: 1,
      direction: 'none',
      isDragging: false,
      isAnimating: false,
    });
  }, []);

  const handleStart = useCallback((clientX: number, clientY: number) => {
    if (opts.disabled) return;
    
    startPos.current = { x: clientX, y: clientY };
    lastPos.current = { x: clientX, y: clientY };
    startTime.current = Date.now();
    
    setState(prev => ({ ...prev, isDragging: true }));
    opts.onSwipeStart?.();
  }, [opts]);

  const handleMove = useCallback((clientX: number, clientY: number) => {
    if (!state.isDragging || opts.disabled) return;

    const deltaX = clientX - startPos.current.x;
    const deltaY = clientY - startPos.current.y;
    
    // Calculate rotation based on horizontal movement
    const rotation = deltaX * (opts.rotationMultiplier || 0.1);
    
    // Calculate opacity based on how far they've swiped
    const progress = Math.abs(deltaX) / (opts.threshold || 100);
    const opacity = Math.max(0.5, 1 - progress * 0.3);
    
    // Determine direction
    const direction = deltaX > 30 ? 'right' : deltaX < -30 ? 'left' : 'none';

    lastPos.current = { x: clientX, y: clientY };

    setState({
      x: deltaX,
      y: deltaY * 0.2, // More restricted vertical movement for stability
      rotation: Math.max(-12, Math.min(12, rotation)), // Capped at 12deg for elegance
      opacity,
      direction,
      isDragging: true,
      isAnimating: false,
    });
  }, [state.isDragging, opts]);

  const handleEnd = useCallback(() => {
    if (!state.isDragging || opts.disabled) return;

    const deltaX = state.x;
    const timeDelta = Date.now() - startTime.current;
    const velocity = Math.abs(deltaX) / timeDelta;
    
    // Check if swipe threshold is met (distance or velocity)
    const swipedLeft = deltaX < -(opts.threshold || 100) || (deltaX < -50 && velocity > (opts.velocityThreshold || 0.5));
    const swipedRight = deltaX > (opts.threshold || 100) || (deltaX > 50 && velocity > (opts.velocityThreshold || 0.5));

    if (swipedLeft) {
      // Animate out to the left
      setState(prev => ({
        ...prev,
        x: -window.innerWidth,
        rotation: -30,
        opacity: 0,
        isDragging: false,
        isAnimating: true,
      }));
      
      // Trigger haptic feedback if available
      if ('vibrate' in navigator) {
        navigator.vibrate(10);
      }
      
      setTimeout(() => {
        opts.onSwipeLeft?.();
        reset();
      }, 300);
    } else if (swipedRight) {
      // Animate out to the right
      setState(prev => ({
        ...prev,
        x: window.innerWidth,
        rotation: 30,
        opacity: 0,
        isDragging: false,
        isAnimating: true,
      }));
      
      // Trigger haptic feedback if available
      if ('vibrate' in navigator) {
        navigator.vibrate(10);
      }
      
      setTimeout(() => {
        opts.onSwipeRight?.();
        reset();
      }, 300);
    } else {
      // Snap back
      setState({
        x: 0,
        y: 0,
        rotation: 0,
        opacity: 1,
        direction: 'none',
        isDragging: false,
        isAnimating: true,
      });
      
      setTimeout(() => {
        setState(prev => ({ ...prev, isAnimating: false }));
      }, 200);
    }

    opts.onSwipeEnd?.();
  }, [state.isDragging, state.x, opts, reset]);

  // Touch handlers
  const onTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    handleStart(touch.clientX, touch.clientY);
  }, [handleStart]);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    handleMove(touch.clientX, touch.clientY);
  }, [handleMove]);

  const onTouchEnd = useCallback(() => {
    handleEnd();
  }, [handleEnd]);

  // Mouse handlers (for desktop testing)
  const onMouseDown = useCallback((e: React.MouseEvent) => {
    handleStart(e.clientX, e.clientY);
  }, [handleStart]);

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    handleMove(e.clientX, e.clientY);
  }, [handleMove]);

  const onMouseUp = useCallback(() => {
    handleEnd();
  }, [handleEnd]);

  const onMouseLeave = useCallback(() => {
    if (state.isDragging) {
      handleEnd();
    }
  }, [state.isDragging, handleEnd]);

  const handlers: SwipeHandlers = {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onMouseDown,
    onMouseMove,
    onMouseUp,
    onMouseLeave,
  };

  return [state, handlers, reset];
}
